// Location Service - utilities for GPS and location tracking

// Get current position using Geolocation API
export const getCurrentPosition = () => {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error('Geolocation is not supported by your browser'));
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        resolve({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy: position.coords.accuracy,
          timestamp: position.timestamp
        });
      },
      (error) => {
        let message = 'Unable to get location';
        switch (error.code) {
          case error.PERMISSION_DENIED:
            message = 'Location permission denied. Please enable location access.';
            break;
          case error.POSITION_UNAVAILABLE:
            message = 'Location information is unavailable.';
            break;
          case error.TIMEOUT:
            message = 'Location request timed out.';
            break;
          default:
            message = 'An unknown error occurred.';
        }
        reject(new Error(message));
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0
      }
    );
  });
};

// Watch position with callbacks - returns a cleanup function
export const watchPosition = (onSuccess, onError, options = {}) => {
  if (!navigator.geolocation) {
    onError(new Error('Geolocation is not supported by your browser'));
    return () => {};
  }

  const defaultOptions = {
    enableHighAccuracy: true,
    timeout: 15000,
    maximumAge: 10000, // Cache position for 10 seconds
    ...options
  };

  const watchId = navigator.geolocation.watchPosition(
    (position) => {
      onSuccess({
        latitude: position.coords.latitude,
        longitude: position.coords.longitude,
        accuracy: position.coords.accuracy,
        speed: position.coords.speed,
        heading: position.coords.heading,
        timestamp: position.timestamp
      });
    },
    (error) => {
      let message = 'Unable to get location';
      switch (error.code) {
        case error.PERMISSION_DENIED:
          message = 'Location permission denied';
          break;
        case error.POSITION_UNAVAILABLE:
          message = 'Location unavailable';
          break;
        case error.TIMEOUT:
          message = 'Location request timed out';
          break;
        default:
          message = 'An unknown error occurred';
      }
      onError(new Error(message));
    },
    defaultOptions
  );

  // Return cleanup function
  return () => {
    navigator.geolocation.clearWatch(watchId);
  };
};

// Calculate distance between two coordinates (Haversine formula)
export const calculateDistance = (lat1, lon1, lat2, lon2) => {
  const R = 6371; // Earth's radius in kilometers
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) *
    Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  const distance = R * c;
  return distance; // Returns distance in kilometers
};

const toRad = (deg) => deg * (Math.PI / 180);

// Format coordinates for display
export const formatCoordinates = (latitude, longitude, precision = 6) => {
  if (!latitude || !longitude) return 'N/A';
  return `${latitude.toFixed(precision)}, ${longitude.toFixed(precision)}`;
};

// Format distance for display
export const formatDistance = (distanceKm) => {
  if (!distanceKm && distanceKm !== 0) return 'N/A';
  if (distanceKm < 1) {
    return `${Math.round(distanceKm * 1000)} m`;
  }
  return `${distanceKm.toFixed(1)} km`;
};

// Calculate ETA based on distance and average speed
export const calculateETA = (distanceKm, avgSpeedKmh = 40) => {
  if (!distanceKm || distanceKm <= 0) return null;
  const hoursRemaining = distanceKm / avgSpeedKmh;
  const minutesRemaining = Math.round(hoursRemaining * 60);

  if (minutesRemaining < 60) {
    return `${minutesRemaining} min`;
  }

  const hours = Math.floor(minutesRemaining / 60);
  const mins = minutesRemaining % 60;
  return `${hours}h ${mins}m`;
};

// Check if location permission is granted
export const checkLocationPermission = async () => {
  if (!navigator.permissions) {
    // Fallback for browsers that don't support permissions API
    return 'prompt';
  }

  try {
    const result = await navigator.permissions.query({ name: 'geolocation' });
    return result.state; // 'granted', 'denied', or 'prompt'
  } catch (error) {
    console.error('Error checking location permission:', error);
    return 'prompt';
  }
};

// Request location permission
export const requestLocationPermission = async () => {
  try {
    await getCurrentPosition();
    return true;
  } catch (error) {
    console.error('Location permission error:', error);
    return false;
  }
};

// Get accuracy indicator text and color
export const getAccuracyIndicator = (accuracyMeters) => {
  if (!accuracyMeters) return { text: 'Unknown', color: 'gray' };

  if (accuracyMeters <= 10) {
    return { text: 'Excellent', color: 'green' };
  } else if (accuracyMeters <= 30) {
    return { text: 'Good', color: 'blue' };
  } else if (accuracyMeters <= 100) {
    return { text: 'Fair', color: 'yellow' };
  } else {
    return { text: 'Poor', color: 'red' };
  }
};

// Convert speed from m/s to km/h
export const convertSpeed = (speedMps) => {
  if (!speedMps && speedMps !== 0) return null;
  return speedMps * 3.6; // Convert m/s to km/h
};

// Format speed for display
export const formatSpeed = (speedKmh) => {
  if (!speedKmh && speedKmh !== 0) return 'N/A';
  return `${Math.round(speedKmh)} km/h`;
};

// Create a Google Maps link for coordinates
export const getGoogleMapsLink = (latitude, longitude) => {
  if (!latitude || !longitude) return null;
  return `https://www.google.com/maps?q=${latitude},${longitude}`;
};

// Create directions link
export const getDirectionsLink = (fromLat, fromLng, toLat, toLng) => {
  if (!fromLat || !fromLng || !toLat || !toLng) return null;
  return `https://www.google.com/maps/dir/${fromLat},${fromLng}/${toLat},${toLng}`;
};

